import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Filter, ChevronDown, X } from "lucide-react";
import type { PartnerSuggestion, ShipmentFilters } from "../../types";
import CompanyFilter from "../reportanalytics/CompanyFilter";
import VehicleFilter from "../reportanalytics/VehicleFilter";
import { useTranslation } from "react-i18next";

interface ShipmentFiltersProps {
  filters: ShipmentFilters;
  selectedStatuses: string[];
  selectedPartners: PartnerSuggestion[];
  selectedAddresses: PartnerSuggestion[];
  truckCarrier: string[];
  trailerCarrier: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
  isServiceIncluded: boolean;
  // New props for direction & route filters
  selectedDirections: string[];
  pickupLocation: string;
  deliveryLocation: string;
  onServiceToggle: (value: boolean) => void;
  onFiltersChange: (newFilters: Partial<ShipmentFilters>) => void;
  onStatusFilter: (status: string) => void;
  onDirectionFilter: (direction: string) => void;
  onPickupLocationChange: (value: string) => void;
  onDeliveryLocationChange: (value: string) => void;
  onPartnerSelect: (company: PartnerSuggestion) => void;
  onAddressSelect: (company: PartnerSuggestion) => void;
  onPartnerRemove: (companyId: string) => void;
  onAddressRemove: (companyId: string) => void;
  onTruckSelect: (truck: string) => void;
  onTruckRemove: (truck: string) => void;
  onTrailerSelect: (trailer: string) => void;
  onTrailerRemove: (trailer: string) => void;
  onDateRangeChange: (start: string, end: string) => void;
  onDateRangeClear: () => void;
  onClearFilters: () => void;
}

// Collapsible section component for compact layout
const FilterSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  badge?: number;
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2.5 text-left group"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-dark-700 uppercase tracking-wide">
            {title}
          </span>
          {badge !== undefined && badge > 0 && (
            <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary text-white rounded-full">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 text-dark-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
};

export const ShipmentFiltersComponent: React.FC<ShipmentFiltersProps> = ({
  selectedStatuses,
  selectedPartners,
  selectedAddresses,
  truckCarrier,
  trailerCarrier,
  isServiceIncluded,
  onServiceToggle,
  dateRangeStart,
  dateRangeEnd,
  selectedDirections,
  pickupLocation,
  deliveryLocation,
  onStatusFilter,
  onDirectionFilter,
  onPickupLocationChange,
  onDeliveryLocationChange,
  onPartnerSelect,
  onAddressSelect,
  onAddressRemove,
  onPartnerRemove,
  onTruckSelect,
  onTruckRemove,
  onTrailerSelect,
  onTrailerRemove,
  onDateRangeChange,
  onDateRangeClear,
  onClearFilters,
}) => {
  const { t } = useTranslation("shipments");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const statuses = ["Order", "AwaitingPickup", "InTransit", "Completed"];
  const directions = ["Inbound", "Outbound"];

  // Count active filters for badge
  const activeFilterCount =
    selectedStatuses.length +
    selectedDirections.length +
    selectedPartners.length +
    selectedAddresses.length +
    truckCarrier.length +
    trailerCarrier.length +
    (dateRangeStart || dateRangeEnd ? 1 : 0) +
    (pickupLocation ? 1 : 0) +
    (deliveryLocation ? 1 : 0) +
    (isServiceIncluded ? 1 : 0);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Filter Button */}
      <button
        type="button"
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className="inline-flex items-center space-x-2 px-4 py-2 border border-dark-300 rounded-lg hover:bg-dark-50 transition-colors"
      >
        <Filter className="h-4 w-4" />
        <span>{t("filters.filters")}</span>
        {activeFilterCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-primary text-white rounded-full">
            {activeFilterCount}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${isFilterOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Filter Dropdown */}
      {isFilterOpen && (
        <div className="absolute right-0 mt-2 w-80 max-md:w-72 bg-white rounded-xl shadow-xl border border-dark-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm font-semibold text-dark-800">
              {t("filters.filters")}
            </span>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                >
                  {t("filters.clearAll")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="text-dark-400 hover:text-dark-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="max-h-[70vh] overflow-y-auto px-4 py-1 space-y-0.5 scrollbar-thin">
            {/* Time Period */}
            <FilterSection
              title={t("filters.timePeriod")}
              defaultOpen={true}
              badge={dateRangeStart || dateRangeEnd ? 1 : 0}
            >
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label
                    className="block text-xs text-dark-500 mb-1"
                    htmlFor="dateStart"
                  >
                    {t("filters.from")}
                  </label>
                  <input
                    type="date"
                    id="dateStart"
                    value={dateRangeStart}
                    max={dateRangeEnd || new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      onDateRangeChange(e.target.value, dateRangeEnd)
                    }
                    className="w-full px-2 py-1.5 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                  />
                </div>
                <div>
                  <label
                    className="block text-xs text-dark-500 mb-1"
                    htmlFor="dateEnd"
                  >
                    {t("filters.to")}
                  </label>
                  <input
                    type="date"
                    id="dateEnd"
                    value={dateRangeEnd}
                    min={dateRangeStart}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      onDateRangeChange(dateRangeStart, e.target.value)
                    }
                    className="w-full px-2 py-1.5 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                  />
                </div>
              </div>
              {(dateRangeStart || dateRangeEnd) && (
                <button
                  type="button"
                  onClick={onDateRangeClear}
                  className="text-xs text-dark-400 hover:text-dark-600 mt-1.5 transition-colors"
                >
                  {t("filters.clearDates")}
                </button>
              )}
            </FilterSection>

            {/* Status */}
            <FilterSection
              title={t("filters.statusTitle")}
              defaultOpen={selectedStatuses.length > 0}
              badge={selectedStatuses.length}
            >
              <div className="grid grid-cols-2 gap-1.5">
                {statuses.map((status) => (
                  <label
                    key={status}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => onStatusFilter(status)}
                      className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                    />
                    <span className="text-xs text-dark-600 leading-none">
                      {status === "AwaitingPickup"
                        ? t("filters.pickedUp")
                        : t(
                            `filters.${status === "InTransit" ? "inTransit" : status === "Completed" ? "completed" : "order"}`,
                          )}
                    </span>
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Direction */}
            <FilterSection
              title={t("filters.direction")}
              defaultOpen={selectedDirections.length > 0}
              badge={selectedDirections.length}
            >
              <div className="grid grid-cols-2 gap-1.5">
                {directions.map((direction) => (
                  <label
                    key={direction}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDirections.includes(direction)}
                      onChange={() => onDirectionFilter(direction)}
                      className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                    />
                    <span className="text-xs text-dark-600 leading-none">
                      {direction === "Inbound"
                        ? t("filters.inbound")
                        : t("filters.outbound")}
                    </span>
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Route */}
            <FilterSection
              title={t("filters.route")}
              defaultOpen={!!(pickupLocation || deliveryLocation)}
              badge={(pickupLocation ? 1 : 0) + (deliveryLocation ? 1 : 0)}
            >
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-dark-500 mb-1">
                    {t("filters.pickupLocation")}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={pickupLocation}
                      onChange={(e) => onPickupLocationChange(e.target.value)}
                      placeholder={t("filters.locationPlaceholder")}
                      className="w-full px-2 py-1.5 pr-7 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs placeholder:text-dark-300"
                    />
                    {pickupLocation && (
                      <button
                        type="button"
                        onClick={() => onPickupLocationChange("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-dark-300 hover:text-dark-500 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-dark-500 mb-1">
                    {t("filters.deliveryLocation")}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={deliveryLocation}
                      onChange={(e) => onDeliveryLocationChange(e.target.value)}
                      placeholder={t("filters.locationPlaceholder")}
                      className="w-full px-2 py-1.5 pr-7 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs placeholder:text-dark-300"
                    />
                    {deliveryLocation && (
                      <button
                        type="button"
                        onClick={() => onDeliveryLocationChange("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-dark-300 hover:text-dark-500 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </FilterSection>

            {/* Partners */}
            <FilterSection
              title={t("filters.partners")}
              defaultOpen={
                selectedPartners.length > 0 || selectedAddresses.length > 0
              }
              badge={selectedPartners.length + selectedAddresses.length}
            >
              <div className="space-y-2">
                <CompanyFilter
                  selectedCompanies={selectedPartners}
                  onCompanySelect={onPartnerSelect}
                  onCompanyRemove={onPartnerRemove}
                  placeholder={t("filters.searchPartners")}
                />
                <CompanyFilter
                  selectedCompanies={selectedAddresses}
                  onCompanySelect={onAddressSelect}
                  onCompanyRemove={onAddressRemove}
                  addresses={true}
                  placeholder={t("filters.searchAddresses")}
                />
              </div>
            </FilterSection>

            {/* Vehicle */}
            <FilterSection
              title={t("filters.vehicle")}
              defaultOpen={truckCarrier.length > 0 || trailerCarrier.length > 0}
              badge={truckCarrier.length + trailerCarrier.length}
            >
              <div className="grid grid-cols-2 gap-2">
                <VehicleFilter
                  selectedCarrier={truckCarrier}
                  onCarrierSelect={onTruckSelect}
                  onCarrierRemove={onTruckRemove}
                  searchField="truckLicensePlate"
                  placeholder={t("filters.searchTruck")}
                />
                <VehicleFilter
                  selectedCarrier={trailerCarrier}
                  onCarrierSelect={onTrailerSelect}
                  onCarrierRemove={onTrailerRemove}
                  searchField="trailerLicensePlate"
                  placeholder={t("filters.searchTrailer")}
                />
              </div>
            </FilterSection>

            {/* Services */}
            <FilterSection
              title={t("filters.options")}
              defaultOpen={isServiceIncluded}
              badge={isServiceIncluded ? 1 : 0}
            >
              <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={isServiceIncluded}
                  onChange={(e) => onServiceToggle(e.target.checked)}
                  className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                />
                <span className="text-xs text-dark-600">
                  {t("filters.includeServices")}
                </span>
              </label>
            </FilterSection>
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <button
              type="button"
              onClick={() => setIsFilterOpen(false)}
              className="w-full text-sm bg-primary text-white py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              {t("filters.applyFilters")}
              {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
